/**
 * Typed in-process event bus for one-shot cross-viewport fan-out.
 *
 * Use when the semantic is "what just happened" (fire-and-forget delivery to
 * every current subscriber), not "what the current value is" (atom). An atom
 * re-evaluates every subscriber on every render that has it as a dep; the bus
 * fires exactly once per emit, regardless of render cycles.
 *
 * Channel payloads are defined in ViewerBusEvents. Adding a channel: extend
 * the interface and update the channel union — the type system enforces that
 * emit and on calls are consistent.
 */

/** All channels and their payload shapes. */
export interface ViewerBusEvents {
  /** SegmentationAnnotationPanel → priority/locked ProtocolViewport.
   *  Fires each time the panel navigates to a finding's slice.
   *  trigger field is intentionally absent — the bus always delivers. */
  sliceNavRequest: { slice: number };
  /** nnInteractive result handler → ProtocolViewport scrollbar orange marker.
   *  Normalized (0-1) per-plane positions for the segmented region center. */
  nnCrossViewportSync: { axial: number; coronal: number; sagittal: number };
  /** R-key / toolbar Reset → per-slot VolumeViewport camera reset. */
  resetSlotView: { slotId: string };
  /** Shared rendering engine canvas → all mounted viewports.
   *  Fires when a WebGL context backing the engine is lost. The engine is kept
   *  alive (restoration requested via preventDefault), so subscribers surface a
   *  TRANSIENT "restoring GPU context" state rather than a terminal reload
   *  prompt. Payload is empty because every loss applies to the single shared
   *  engine. Paired with `webglContextRestored`. */
  webglContextLost: Record<string, never>;
  /** Shared rendering engine canvas → all mounted viewports.
   *  Fires when the lost WebGL context is restored (vtk rebuilds the render
   *  window). Subscribers clear the transient state and re-attach their volume/
   *  stack, since the GPU textures were dropped with the context. Empty payload,
   *  single shared engine. */
  webglContextRestored: Record<string, never>;
  /** Any surface (viewer tool, command, reporter dictation) → the single
   *  page-level toast host (PageToast). Fire-and-forget: each emit shows the
   *  message and re-arms the auto-dismiss timer, so repeating the same string
   *  re-shows it — which a shared "current value" atom could not (an unchanged
   *  value triggers no re-render). One host subscribes at the App layout.
   *  `action` renders an inline button (e.g. bulk-delete Undo); `durationMs`
   *  overrides the default auto-dismiss for toasts that need reaction time. */
  toast: {
    message: string;
    action?: { label: string; onPress: () => void };
    durationMs?: number;
  };
  /** SeriesThumbnail pointer-drag → the ProtocolViewport slot under the cursor
   *  at release. Pointer-based drag replaces native HTML5 DnD (which Safari/
   *  WebKit won't initiate on a <div>). The matching-slot viewport runs the
   *  series assignment. */
  seriesPointerDrop: { seriesId: string; slotId: string };
  /** Sidebar or viewport menu asked for a reformat (MPR) of a series to any
   *  plane. The grid owns the destination slot and the acquisition-group
   *  guard, so the request travels as an intent — plane, not slot — and
   *  HangingProtocolGrid resolves the rest. Kept a literal union (not
   *  `PlaneOrientation`) deliberately — this module imports nothing from the
   *  app. */
  seriesReformatRequest: { seriesId: string; plane: "Axial" | "Coronal" | "Sagittal" };
}

export type ViewerBusChannel = keyof ViewerBusEvents;
type Handler<C extends ViewerBusChannel> = (payload: ViewerBusEvents[C]) => void;

// One Set<Handler> per channel. Map keys are channel name strings.
const subs = new Map<string, Set<Handler<ViewerBusChannel>>>();

function getOrCreateSet<C extends ViewerBusChannel>(channel: C): Set<Handler<C>> {
  let s = subs.get(channel) as Set<Handler<C>> | undefined;
  if (!s) {
    s = new Set();
    subs.set(channel, s as Set<Handler<ViewerBusChannel>>);
  }
  return s;
}

/** Subscribe to a channel. Returns the same handler for use with `off`. */
export function on<C extends ViewerBusChannel>(channel: C, handler: Handler<C>): Handler<C> {
  getOrCreateSet(channel).add(handler);
  return handler;
}

/** Unsubscribe a previously-registered handler. No-op if not registered. */
export function off<C extends ViewerBusChannel>(channel: C, handler: Handler<C>): void {
  getOrCreateSet(channel).delete(handler);
}

/**
 * Emit an event. Every current subscriber is called synchronously.
 *
 * Returns the number of handlers called (0 on the no-subscriber path) so a
 * caller can detect a delivered-to-nobody emit and say so instead of dropping
 * the intent on the floor silently. Backward compatible — every existing
 * caller ignores the return value.
 */
export function emit<C extends ViewerBusChannel>(channel: C, payload: ViewerBusEvents[C]): number {
  const set = subs.get(channel) as Set<Handler<C>> | undefined;
  if (!set) return 0;
  // Snapshot before iteration — a handler's off() call during delivery must
  // not mutate the live set mid-loop.
  const handlers = Array.from(set);
  for (const handler of handlers) {
    handler(payload);
  }
  return handlers.length;
}

/**
 * Remove all subscribers for a channel.
 * Intended for test isolation only — do NOT call in production code.
 */
export function _testOnlyClearChannel(channel: ViewerBusChannel): void {
  subs.delete(channel);
}
